// React imports
import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

//  material-ui imports
import {
    CircularProgress,
    InputAdornment,
    TextField,
    Typography,
    Stack,
    Box,
    IconButton,
    Card,
    Paper,
    TableContainer,
    Table,
    TableHead,
    TableCell,
    TableBody,
    TableRow,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// third-party imports
import { useIntl } from 'react-intl';
import { useDropzone } from 'react-dropzone';
import { dispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import FileMngrTableRowItem from './components/FileMngrTableRowItem';
import FileMngrDialogSpecialPaste from './dialog/FileMngrDialogSpecialPaste';
import { v4 as uuidv4 } from 'uuid';

// project imports
import FileMngrToolbarButton from './toolbar/FileMngrToolbarButton';
import gestopAPI from 'utils/gestopAPI';
import FileMngrPathBreadcrumb from './breadcrumb';
import FileMngrDialogNewFolder from './dialog/FileMngrDialogNewFolder';
import FileMngrDialogRename from './dialog/FileMngrDialogRename';
import FileMngrDialogUpload, { fileMngrHandleUploadFiles } from './dialog/FileMngrDialogUpload';
import FileMngrDialogViewDetails from './dialog/FileMngrDialogViewDetails';
import FileMngrDialogDeleteConfirmation from './dialog/FileMngrDialogDeleteConfirmation';
import fileMngrRenderSortByMenu from './renders/fileMngrRenderSortByMenu';
import FileMngrToolbar from './toolbar/FileMngrToolbar';
import fileMngrRenderContextMenu from './renders/fileMngrRenderContextMenu';
import {
    appendFileMngrQuery,
    fileMngrItemId,
    fileMngrGetTempUrlParameters,
    getFileMngrItemParentPath,
    getFileMngrErrorMessage,
    isFileMngrResponseSuccessful,
    joinFileMngrPath,
    normalizeFileMngrPath
} from './utils/fileMngrPath';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';

// material-ui icons
import { ClearAll, DownloadingTwoTone } from '@mui/icons-material';
import FolderIcon from '@mui/icons-material/Folder';
import UploadIcon from '@mui/icons-material/Upload';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import SortIcon from '@mui/icons-material/Sort';
import RefreshIcon from '@mui/icons-material/Cached';
import DeleteIcon from '@mui/icons-material/Delete';
// import ViewModuleIcon from '@mui/icons-material/ViewModule';
// import ViewListIcon from '@mui/icons-material/ViewList';
import InfoIcon from '@mui/icons-material/Info';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ContentPasteGoTwoTone from '@mui/icons-material/ContentPasteGoTwoTone';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

// ==============================|| FILEMNGR ITEM NAME ||============================== //

export const changeFileMngrItemName = (name, intl) => {
    if (typeof name !== 'string') return '';
    if (!intl) return name;

    switch (name) {
        case 'busopp-files':
            return intl.formatMessage({ id: 'ui-comp-filemngr-busopp-files-label' });
        case 'business-opportunities':
            return intl.formatMessage({ id: 'ui-comp-filemngr-busopp-files-label' });
        case 'workorder-files':
            return intl.formatMessage({ id: 'ui-comp-filemngr-workorder-files-label' });
        case 'workorders-files':
            return intl.formatMessage({ id: 'ui-comp-filemngr-workorder-files-label' });
        case 'workmgmt-files':
            return intl.formatMessage({ id: 'ui-comp-filemngr-workmgmt-files-label' });
        case 'workmanagement-files':
            return intl.formatMessage({ id: 'ui-comp-filemngr-workmgmt-files-label' });
        case 'client-files':
            return intl.formatMessage({ id: 'ui-comp-filemngr-client-files-label' });
        case 'clients-files':
            return intl.formatMessage({ id: 'ui-comp-filemngr-client-files-label' });
        case 'note-files':
            return intl.formatMessage({ id: 'ui-comp-filemngr-note-files-label' });
        case 'notes-files':
            return intl.formatMessage({ id: 'ui-comp-filemngr-note-files-label' });
        case 'task-files':
            return intl.formatMessage({ id: 'ui-comp-filemngr-task-files-label' });
        default:
            return name;
    }
};

export const isSystemFolder = (name) => {
    if (typeof name !== 'string') return false;

    switch (name) {
        case 'client-files':
        case 'busopp-files':
        case 'workorder-files':
        case 'task-files':
        case 'note-files':
        case 'avatar-file':
            return true;
        default:
            return /^[a-zA-Z]+-\d+$/.test(name);
    }
};

export { fileMngrGetTempUrlParameters };

// ==============================|| FILEMNGR CONTAINER ||============================== //

const FileMngrContainer = ({ children, border, setIsLoading, currentPath, uploadUrl, handleRefreshFolder, ...props }) => {
    const intl = useIntl();
    const theme = useTheme();

    const canUpload = Boolean(uploadUrl);

    const handleDroppedFile = async (droppedFiles) => {
        if (!canUpload || !droppedFiles?.length) {
            return;
        }

        await fileMngrHandleUploadFiles(
            droppedFiles,
            null,
            dispatch,
            intl,
            setIsLoading,
            currentPath,
            uploadUrl,
            handleRefreshFolder,
            () => {}
        );
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: handleDroppedFile,
        maxSize: 10000000,
        disabled: !canUpload,
        noClick: true
    });

    return (
        <Card
            sx={{
                border: border ? '1px solid' : 'none',
                borderColor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.primary[200] + 75,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0
            }}
            {...getRootProps()}
            {...props}
            onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
            }}
        >
            {isDragActive && (
                <Paper
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: theme.palette.primary[200] + 40,
                        opacity: 0.8,
                        zIndex: 1000
                    }}
                >
                    <Typography variant="h2">{intl.formatMessage({ id: 'ui-comp-filemngr-dialog-uploadfiles-drop-text' })}</Typography>
                </Paper>
            )}
            <input type="file" multiple style={{ display: 'none' }} {...getInputProps()} />
            <Stack sx={{ display: 'flex', flex: 1, minHeight: 0 }}>{children}</Stack>
        </Card>
    );
};

FileMngrContainer.propTypes = {
    children: PropTypes.node,
    props: PropTypes.any,
    border: PropTypes.bool,
    currentPath: PropTypes.any,
    uploadUrl: PropTypes.any,
    handleRefreshFolder: PropTypes.func,
    setIsLoading: PropTypes.func
};

export const downloadFile = (data, fileName, contentType) => {
    const blob = new Blob([data], { type: contentType });
    const href = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', fileName);
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
};

// ==============================|| FILEMNGR ||============================== //

/**
 * FileMngr component
 * @author Marcos L. A. Soto <marcosleonildo@gmail.com>
 * @param {string} url - url for the file manager
 * @param {string} uploadUrl - url for the file manager upload
 * @param {string} downloadUrl - url for the file manager download
 * @param {string} getImageUrl - url for the file manager get image
 * @param {object} permissionData - permission data for the file manager
 * @param {boolean} isModal - modal view for the file manager
 * @param {boolean} onChangeTempFoldersName - track temp folders name
 */
const FileMngr = ({
    url,
    uploadUrl,
    downloadUrl,
    getImageUrl,
    permissionData,
    isModal,
    dialogItemToPaste,
    clearDialogItemToPaste,
    targetClass,
    targetId,
    workWithTemp,
    forceReload,
    onChangeTempFoldersName
}) => {
    const intl = useIntl();
    const theme = useTheme();
    const tempFoldersNameRef = useRef(uuidv4());
    const isMmDown = useMediaQuery(theme.breakpoints.down('md'));

    const [selectedItem, setSelectedItem] = useState(null);
    const [multipleSelectedItems, setMultipleSelectedItems] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [openNewFolderDialog, setOpenNewFolderDialog] = useState(false);
    const [folderContent, setFolderContent] = useState(null);
    const [folderContentSearch, setFolderContentSearch] = useState(null);
    const [currentPath, setCurrentPath] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [sortBy, setSortBy] = useState('name');
    const [sortType, setSortType] = useState('asc');
    const [contextMenu, setContextMenu] = React.useState(null);
    const [openRenameDialog, setOpenRenameDialog] = useState(false);
    const [openUploadDialog, setOpenUploadDialog] = useState(false);
    const [openViewDetailsDialog, setOpenViewDetailsDialog] = useState(false);
    const [openSpecialPasteDialog, setOpenSpecialPasteDialog] = useState(false);
    const [itemToPaste, setItemToPaste] = useState(null);
    const [sortByMenuAnchorEl, setSortByMenuAnchorEl] = useState(null);
    const [isPasting, setIsPasting] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const folderContentReference = () => (isSearching === true && searchText.length > 0 ? folderContentSearch : folderContent);

    const buildManagedUrl = (baseUrl) => {
        if (!baseUrl) {
            return null;
        }

        return appendFileMngrQuery(
            baseUrl,
            workWithTemp
                ? {
                      workingWithTemp: 1,
                      tempFoldersName: tempFoldersNameRef.current,
                      targetIsTemp: 1
                  }
                : {}
        );
    };

    const fileMngrUrls = useMemo(
        () => ({
            url: buildManagedUrl(url),
            uploadUrl: buildManagedUrl(uploadUrl),
            downloadUrl: buildManagedUrl(downloadUrl),
            getImageUrl: buildManagedUrl(getImageUrl)
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [url, uploadUrl, downloadUrl, getImageUrl, workWithTemp]
    );

    const { canAdd, canEdit, canDelete } = useMemo(
        () => ({
            canAdd: permissionData?.includes('canAdd'),
            canEdit: permissionData?.includes('canEdit'),
            canDelete: permissionData?.includes('canDelete')
        }),
        [permissionData]
    );

    const BORDER_COLOR = theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.primary[200] + 75;

    const SORT_ITEMS_BY = {
        NAME: 'name',
        SIZE: 'size',
        DATE_MODIFIED: 'dateModified',
        DATE_CREATED: 'dateCreated'
    };

    const SORT_TYPE = {
        ASC: 'asc',
        DESC: 'desc'
    };

    const SORT_BY_MENU = [
        {
            name: intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-sortby-submenu-name' }),
            value: SORT_ITEMS_BY.NAME,
            isSortType: false
        },
        {
            name: intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-sortby-submenu-size' }),
            value: SORT_ITEMS_BY.SIZE,
            isSortType: false
        },
        {
            name: intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-sortby-submenu-datemodified' }),
            value: SORT_ITEMS_BY.DATE_MODIFIED,
            isSortType: false
        },
        {
            name: intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-sortby-submenu-datecreated' }),
            value: SORT_ITEMS_BY.DATE_CREATED,
            isSortType: false
        },
        {
            name: '',
            value: 'separator',
            isSortType: false
        },
        {
            name: intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-sortby-submenu-ascending' }),
            value: SORT_TYPE.ASC,
            isSortType: true
        },
        {
            name: intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-sortby-submenu-descending' }),
            value: SORT_TYPE.DESC,
            isSortType: true
        }
    ];

    const isItemSelected = () => selectedItem !== null;

    // ==============================|| HANDLE OPEN NEW FOLDER DIALOG ||============================== //

    const handleOpenNewFolderDialog = () => setOpenNewFolderDialog(true);
    const handleCloseNewFolderDialog = () => setOpenNewFolderDialog(false);

    // =============================================================================================== //

    // ==============================|| HANDLE OPEN RENAME DIALOG ||============================== //

    const handleOpenRenameDialog = () => {
        if (!selectedItem || typeof selectedItem.name !== 'string') {
            dispatch(
                openSnackbar({
                    open: true,
                    message: intl.formatMessage({ id: 'ui-comp-filemngr-item-invalid-selecton' }),
                    variant: 'alert',
                    alert: { color: 'error' }
                })
            );
            return;
        }

        setOpenRenameDialog(true);
    };
    const handleCloseRenameDialog = () => setOpenRenameDialog(false);

    // =============================================================================================== //

    // ==============================|| HANDLE OPEN UPLOAD DIALOG ||============================== //

    const handleOpenUploadDialog = () => setOpenUploadDialog(true);
    const handleCloseUploadDialog = () => setOpenUploadDialog(false);

    // =============================================================================================== //

    // ==============================|| HANDLE OPEN VIEW DETAILS ||============================== //

    const handleOpenViewDetailsDialog = () => setOpenViewDetailsDialog(true);
    const handleCloseViewDetailsDialog = () => setOpenViewDetailsDialog(false);

    // =============================================================================================== //

    // ==============================|| HANDLE OPEN SORT BY MENU ||============================== //

    const handleOpenSortByMenu = (event) => setSortByMenuAnchorEl(event.currentTarget);
    const handleCloseSortByMenu = () => setSortByMenuAnchorEl(null);

    // =============================================================================================== //

    // ==============================|| HANDLE READ FOLDERRS AND PATHS ||============================== //

    const generatePath = (currentPathArray, index) => {
        if (!Array.isArray(currentPathArray) || currentPathArray.length === 0 || index < 1) {
            return '/';
        }

        return normalizeFileMngrPath(
            currentPathArray
                .slice(1, index + 1)
                .map((item) => item?.name)
                .join('/')
        );
    };

    const getCurrentPath = () =>
        normalizeFileMngrPath(
            Array.isArray(currentPath)
                ? currentPath
                      .slice(1)
                      .map((item) => item?.name)
                      .join('/')
                : '',
            { trailingSlash: true }
        );

    const getItemParentPath = (item) => (isSearching ? getFileMngrItemParentPath(item, getCurrentPath()) : getCurrentPath());

    const readFolder = async (path, search = false) => {
        try {
            setSelectedItem(null);
            setIsLoading(true);
            const data = {
                action: 'read',
                path: typeof path === 'string' ? path : '/',
                showHiddenItems: false,
                data: [],
                ...(isModal && targetClass && targetId ? { targetClass, targetId } : {}),
                ...(search ? { searchText } : {})
            };

            const response = await gestopAPI.post(fileMngrUrls.url, data);
            if (!isFileMngrResponseSuccessful(response)) {
                throw new Error(getFileMngrErrorMessage(response) ?? 'Unable to read folder.');
            }

            return {
                cwd: {
                    ...(response.data?.cwd ?? {}),
                    name: response.data?.cwd?.name ?? '',
                    filterPath: response.data?.cwd?.filterPath ?? ''
                },
                files: Array.isArray(response.data?.files)
                    ? response.data.files.map((item) => ({
                          ...item,
                          name: item?.name ?? '',
                          filterPath: item?.filterPath ?? ''
                      }))
                    : []
            };
        } catch (error) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: intl.formatMessage({ id: 'ui-comp-filemngr-readfolder-error' }),
                    variant: 'alert',
                    alert: { color: 'error' }
                })
            );
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const handleReadFolder = async (folder = '', refreshOnly = false, search = false) => {
        if (isModal && (!targetClass || !targetId)) return;

        const folderResponse = await readFolder(folder, search);

        if (search === true) {
            setFolderContentSearch(() => folderResponse);
            setIsSearching(true);
        } else {
            setFolderContent(() => folderResponse);
            setIsSearching(false);
            setSearchText('');
        }

        if (folderResponse && !refreshOnly) {
            const normalizedFolder = normalizeFileMngrPath(folder);

            if (normalizedFolder === '/') {
                setCurrentPath(() => [{ name: folderResponse?.cwd?.name, filterPath: '' }]);
            } else {
                setCurrentPath((previousPath) => [
                    previousPath?.[0] ?? { name: folderResponse?.cwd?.name ?? '', filterPath: '' },
                    ...normalizedFolder
                        .split('/')
                        .filter(Boolean)
                        .map((name) => ({ name, filterPath: '' }))
                ]);
            }
        }
    };

    const openFolder = (item) => {
        if (item?.isFile === false) {
            handleReadFolder(joinFileMngrPath(getItemParentPath(item), item.name));
        }
    };

    const handleRefreshFolder = async () => {
        if (Array.isArray(currentPath) && currentPath?.length >= 1) {
            await handleReadFolder(getCurrentPath(), true, false);
        }
    };

    // =============================================================================================== //

    // ==============================|| HANDLE CLEAR SEARCH TEXT ||============================== //

    const handleClearSearchText = () => {
        setSearchText('');
        setIsSearching(false);
        setFolderContentSearch(null);
    };

    // =============================================================================================== //

    // ==============================|| HANDLE SELECTED ITEMS OPERATIONS ||============================== //

    const handleCopyItem = async (selectedItem, currentPath) => {
        if (!selectedItem || typeof selectedItem !== 'object' || typeof selectedItem.name !== 'string') {
            dispatch(
                openSnackbar({
                    open: true,
                    message: intl.formatMessage({ id: 'ui-comp-filemngr-item-invalid-selecton' }),
                    variant: 'alert',
                    alert: { color: 'error' }
                })
            );
            return;
        }

        const itemToPasteRef = {
            id: selectedItem?.id,
            ids: multipleSelectedItems?.length > 0 ? multipleSelectedItems.map((item) => item?.id) : [selectedItem?.id],
            isToCopy: true,
            isToCut: false,
            data: multipleSelectedItems?.length > 0 ? multipleSelectedItems : [selectedItem],
            names: multipleSelectedItems?.length > 0 ? multipleSelectedItems.map((item) => item?.name) : [selectedItem?.name],
            path: currentPath ?? '/',
            targetData: null,
            targetPath: null,
            isFile: selectedItem?.isFile,
            isFiles: multipleSelectedItems?.length > 0 ? multipleSelectedItems.map((item) => item?.isFile) : []
        };

        setItemToPaste(itemToPasteRef);
    };

    const itemIdGenerator = ({ isFile, name, index }) => fileMngrItemId({ isFile, name }, index);

    const isFMItemCutted = (id) => itemToPaste?.id === id && itemToPaste?.isToCut === true;

    const handleCutItem = async (selectedItem, currentPath) => {
        if (!selectedItem || typeof selectedItem !== 'object' || typeof selectedItem.name !== 'string') {
            dispatch(
                openSnackbar({
                    open: true,
                    message: intl.formatMessage({ id: 'ui-comp-filemngr-item-invalid-selecton' }),
                    variant: 'alert',
                    alert: { color: 'error' }
                })
            );
            return;
        }

        const itemToPasteRef = {
            id: selectedItem?.id,
            ids: multipleSelectedItems?.length > 0 ? multipleSelectedItems.map((item) => item?.id) : [selectedItem?.id],
            isToCopy: false,
            isToCut: true,
            data: multipleSelectedItems?.length > 0 ? multipleSelectedItems : [selectedItem],
            names: multipleSelectedItems?.length > 0 ? multipleSelectedItems.map((item) => item?.name) : [selectedItem?.name],
            path: currentPath ?? '/',
            targetData: null,
            targetPath: null,
            isFile: selectedItem?.isFile,
            isFiles: multipleSelectedItems?.length > 0 ? multipleSelectedItems.map((item) => item?.isFile) : []
        };

        setItemToPaste(itemToPasteRef);

        handleClearSearchText();
    };

    const canPaste = () =>
        isLoading || !itemToPaste?.names?.length || joinFileMngrPath(itemToPaste?.path, itemToPaste?.names[0]) === getCurrentPath();

    const handlePasteItem = async (dataToPaste) => {
        setIsPasting(true);
        setSelectedItem(null);

        const itemToPasteCopy = itemToPaste ?? dialogItemToPaste;

        const data =
            typeof dataToPaste === 'object' && 'action' in dataToPaste
                ? {
                      action: dataToPaste.action,
                      data: dataToPaste.data,
                      names: dataToPaste.names,
                      path: dataToPaste.path,
                      renameFiles: dataToPaste.renameFiles,
                      targetData: dataToPaste.targetData,
                      targetPath: dataToPaste.targetPath,
                      ...(isModal && targetClass && targetId ? { targetClass, targetId } : {}),
                      ...dataToPaste
                  }
                : {
                      action: itemToPasteCopy?.isToCut === true ? 'move' : 'copy',
                      data: itemToPasteCopy?.data,
                      names: itemToPasteCopy?.names,
                      path: itemToPasteCopy?.path,
                      renameFiles: [],
                      targetData: null,
                      targetPath: getCurrentPath(),
                      ...(isModal && targetClass && targetId ? { targetClass, targetId } : {})
                  };

        try {
            const response = await gestopAPI.post(fileMngrUrls.url, data);
            if (!isFileMngrResponseSuccessful(response)) {
                throw new Error(getFileMngrErrorMessage(response) ?? 'Unable to paste files.');
            }

            await handleRefreshFolder();
            setItemToPaste(null);
            setMultipleSelectedItems([]);
            setSelectedItem(null);

            if (typeof clearDialogItemToPaste === 'function') {
                clearDialogItemToPaste();
            }
        } catch (error) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: error?.message || intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                    variant: 'alert',
                    alert: { color: 'error' }
                })
            );
        } finally {
            setIsPasting(false);
        }
    };

    const handleSpecialPasteItem = async () => {
        setOpenSpecialPasteDialog(true);
    };

    // =============================================================================================== //

    const sortItems = (items = [], sortBy, sortType) => {
        const sortedItems = [...items]; // Create a copy of the original array to avoid modifying it directly
        sortedItems.sort((a, b) => {
            if (sortBy === SORT_ITEMS_BY.NAME) {
                return sortType === SORT_TYPE.ASC ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
            }
            if (sortBy === SORT_ITEMS_BY.SIZE) {
                return sortType === SORT_TYPE.ASC ? a.size - b.size : b.size - a.size;
            }
            if (sortBy === SORT_ITEMS_BY.DATE_MODIFIED) {
                return sortType === SORT_TYPE.ASC
                    ? new Date(a.dateModified).getTime() - new Date(b.dateModified).getTime()
                    : new Date(b.dateModified).getTime() - new Date(a.dateModified).getTime();
            }
            if (sortBy === SORT_ITEMS_BY.DATE_CREATED) {
                return sortType === SORT_TYPE.ASC
                    ? new Date(a.dateCreated).getTime() - new Date(b.dateCreated).getTime()
                    : new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime();
            }
            return 0;
        });
        return sortedItems;
    };

    const handleUnselectItem = () => {
        setSelectedItem(null);
        setMultipleSelectedItems([]);
    };

    // ==============================|| HANDLE DELETE ITEM ||============================== //

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const handleDeleteDialogOpen = () => {
        setDeleteDialogOpen(true);
    };

    const handleDeleteDialogClose = () => {
        setDeleteDialogOpen(false);
    };

    const handleDelete = async () => {
        handleDeleteDialogOpen();
    };

    const deleteItem = async ({ item, items, setItem, setItems }) => {
        const itemsToDelete = Array.isArray(items) ? items : [];
        const itemsForRequest = itemsToDelete.length > 1 ? itemsToDelete : [item].filter(Boolean);

        if (itemsForRequest.length === 0) {
            return;
        }

        const data = {
            action: 'delete',
            data: itemsForRequest,
            names: itemsForRequest.map((currentItem) => currentItem?.name).filter(Boolean),
            path: getCurrentPath(),
            ...(isModal && targetClass && targetId ? { targetClass, targetId } : {})
        };

        if (setItem && typeof setItem === 'function') {
            const response = await gestopAPI.post(fileMngrUrls.url, data);

            if (!isFileMngrResponseSuccessful(response)) {
                throw new Error(getFileMngrErrorMessage(response) ?? 'Unable to delete files.');
            }

            await handleRefreshFolder();
            setItem(null);
            setItems([]);
        }
    };

    // =============================================================================================== //

    // ==============================|| HANDLE DOWNLOAD ITEM ||============================== //

    const prepareDownload = (response, items = []) => {
        const contentType = response.headers?.['content-type'] ?? 'application/octet-stream';
        const contentDisposition = response.headers?.['content-disposition'];
        const isMultiple = items.length > 1 || items[0]?.isFile === false;
        let fileName = items[0]?.name ?? 'download';

        if (contentDisposition) {
            const match = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^;"]+)/i);
            if (match) {
                fileName = decodeURIComponent(match[1].replace(/"/g, '').trim());
            }
        }

        if (isMultiple) {
            fileName = `${currentPath[currentPath?.length - 1]?.name || 'download'}.zip`;
        }

        downloadFile(response.data, fileName, contentType);
    };

    const handleDownload = async (item = null) => {
        const itemToDownload = typeof item?.isFile === 'boolean' ? item : null;
        let items = [];
        if (itemToDownload) {
            items = [itemToDownload];
        } else if (multipleSelectedItems?.length > 1) {
            items = multipleSelectedItems;
        } else if (selectedItem) {
            items = [selectedItem];
        }

        if (items.length > 0 && fileMngrUrls.downloadUrl) {
            const itemPaths = [...new Set(items.map((currentItem) => getItemParentPath(currentItem)))];

            if (itemPaths.length !== 1) {
                dispatch(
                    openSnackbar({
                        open: true,
                        message: intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                        variant: 'alert',
                        alert: { color: 'error' }
                    })
                );
                return;
            }

            setIsDownloading(true);

            const data = {
                action: 'download',
                path: itemPaths[0],
                names: items.map((currentItem) => currentItem?.name).filter(Boolean),
                data: items
            };

            try {
                const response = await gestopAPI.post(fileMngrUrls.downloadUrl, data, { responseType: 'blob' });
                prepareDownload(response, items);
            } catch (error) {
                let message = getFileMngrErrorMessage(error?.response);

                if (!message && error?.response?.data instanceof Blob) {
                    try {
                        message = getFileMngrErrorMessage({ data: JSON.parse(await error.response.data.text()) });
                    } catch {
                        message = null;
                    }
                }

                dispatch(
                    openSnackbar({
                        open: true,
                        message: message || intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                        variant: 'alert',
                        alert: { color: 'error' }
                    })
                );
            } finally {
                setIsDownloading(false);
            }
        }
    };

    // =============================================================================================== //

    // ==============================|| HANDLE SEARCH TEXTFIELD ||============================== //

    const handleSubmitSearch = async (e) => {
        e.preventDefault();
        await handleReadFolder(getCurrentPath(), null, true);
    };

    const handleChangeSearchText = (e) => {
        setSearchText(e.target.value);

        if (e.target.value === '' && isSearching === true && folderContentSearch) {
            setIsSearching(false);
            setFolderContentSearch(null);
        }
    };

    const handleRemoveFromContent = (isFile, name, index) => {
        const filtered = folderContentReference()?.files.filter(
            (i, idx) => itemIdGenerator({ isFile, name, index }) !== itemIdGenerator({ isFile: i.isFile, name: i.name, index: idx })
        );
        if (isSearching && searchText?.length > 0) {
            setFolderContentSearch(() => ({ ...folderContentSearch, files: filtered }));
        } else {
            setFolderContent(() => ({ ...folderContent, files: filtered }));
        }
    };

    const FILE_CONTEXT_MENU = [
        {
            name: intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-rename' }),
            value: 'rename',
            onClick: handleOpenRenameDialog,
            disabled: !canEdit
        },
        { name: '', value: 'divider', onClick: () => {} },
        {
            name: intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-cut' }),
            value: 'cut',
            onClick: () => handleCutItem(selectedItem, getCurrentPath()),
            disabled: !canEdit
        },
        {
            name: intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-copy' }),
            value: 'copy',
            onClick: () => handleCopyItem(selectedItem, getCurrentPath()),
            disabled: !canEdit
        },
        // { name: intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-paste' }), value: 'paste', onClick: handlePasteItem },
        { name: '', value: 'divider', onClick: () => {} },
        {
            name: intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-download' }),
            value: 'download',
            onClick: handleDownload,
            disabled: isDownloading
        },
        {
            name: intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-details' }),
            value: 'details',
            onClick: handleOpenViewDetailsDialog
        },
        { name: '', value: 'divider', onClick: () => {} },
        {
            name: intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-delete' }),
            value: 'delete',
            onClick: handleDelete,
            disabled: !canDelete
        }
    ];

    const FOLDER_CONTEXT_MENU = [
        {
            name: intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-open' }),
            value: 'open',
            onClick: () => openFolder(selectedItem)
        },
        { name: '', value: 'divider', onClick: () => {} },
        {
            name: intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-rename' }),
            value: 'rename',
            onClick: handleOpenRenameDialog,
            disabled: !canEdit || isSystemFolder(selectedItem?.name)
        },
        {
            name: intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-cut' }),
            value: 'cut',
            onClick: () => handleCutItem(selectedItem, getItemParentPath(selectedItem)),
            disabled: !canEdit || isSystemFolder(selectedItem?.name)
        },
        {
            name: intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-copy' }),
            value: 'copy',
            onClick: () => handleCopyItem(selectedItem, getItemParentPath(selectedItem)),
            disabled: !canEdit || isSystemFolder(selectedItem?.name)
        },
        // { name: intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-paste' }), value: 'paste', onClick: handlePasteItem },
        { name: '', value: 'divider', onClick: () => {} },
        {
            name: intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-download' }),
            value: 'download',
            onClick: handleDownload,
            disabled: isDownloading
        },
        { name: '', value: 'divider', onClick: () => {} },
        {
            name: intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-delete' }),
            value: 'delete',
            onClick: handleDelete,
            disabled: !canDelete || isSystemFolder(selectedItem?.name)
        },
        {
            name: intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-details' }),
            value: 'details',
            onClick: handleOpenViewDetailsDialog
        }
    ];

    const getSelectedItem = (item, isFile, index) => ({
        id: itemIdGenerator({ isFile, name: item?.name, index }),
        ...item
    });

    const handleItemSelection = (event, item, isFile, index) => {
        event.stopPropagation();

        const itemToSelect = getSelectedItem(item, isFile, index);

        if (event.ctrlKey || event.metaKey) {
            setMultipleSelectedItems((previousItems) => {
                const selectedItems = previousItems.length === 0 && selectedItem !== null ? [{ ...selectedItem }] : previousItems;

                return selectedItems.some((selected) => selected.id === itemToSelect.id) ? selectedItems : [...selectedItems, itemToSelect];
            });
            return;
        }

        if (multipleSelectedItems.length > 0) {
            setSelectedItem(itemToSelect);
        } else {
            setSelectedItem(selectedItem?.id === itemToSelect.id ? null : itemToSelect);
        }

        setMultipleSelectedItems([]);
    };

    const isSelected = (isFile, name, index) =>
        itemIdGenerator({ isFile, name, index }) === selectedItem?.id ||
        (multipleSelectedItems?.length > 0 &&
            multipleSelectedItems.findIndex((i) => i?.id === itemIdGenerator({ isFile, name, index })) > -1);

    useEffect(() => {
        if (multipleSelectedItems?.length === 1) setSelectedItem(multipleSelectedItems[0]);
    }, [multipleSelectedItems]);

    useEffect(() => {
        if (isModal) {
            if (targetClass && targetId) {
                handleReadFolder('/');
            } else if (openSpecialPasteDialog === false && dialogItemToPaste?.action === 'move') {
                handleReadFolder(null);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetId, openSpecialPasteDialog]);

    // useEffect(() => {
    //     handleReadFolder(null);
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [dialogItemToPaste]);

    useEffect(() => {
        handleReadFolder('/');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forceReload]);

    useEffect(() => {
        if (
            workWithTemp &&
            typeof tempFoldersNameRef.current === 'string' &&
            tempFoldersNameRef.current.length > 0 &&
            typeof onChangeTempFoldersName === 'function'
        ) {
            onChangeTempFoldersName(tempFoldersNameRef.current);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workWithTemp, tempFoldersNameRef]);

    return (
        <FileMngrContainer
            border
            handleRefreshFolder={handleRefreshFolder}
            currentPath={getCurrentPath()}
            uploadUrl={fileMngrUrls.uploadUrl}
            setIsLoading={setIsLoading}
        >
            {/* PRIMARY TOOLBAR */}
            <FileMngrToolbar>
                {!isModal && isItemSelected() && multipleSelectedItems.length === 0 && selectedItem?.isFile === false && (
                    <FileMngrToolbarButton
                        onClick={() => openFolder(selectedItem)}
                        text={intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-open' })}
                        icon={FolderOpenIcon}
                    />
                )}
                {!isModal && !isItemSelected() && canEdit && (
                    <FileMngrToolbarButton
                        onClick={handleOpenNewFolderDialog}
                        text={intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-new-folder' })}
                        icon={FolderIcon}
                    />
                )}
                {!isModal && isItemSelected() && multipleSelectedItems.length === 0 && canEdit && (
                    <FileMngrToolbarButton
                        onClick={handleOpenRenameDialog}
                        text={intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-rename' })}
                        icon={DriveFileRenameOutlineIcon}
                        disabled={isSystemFolder(selectedItem?.name)}
                    />
                )}
                {!isModal && !isItemSelected() && canAdd && (
                    <FileMngrToolbarButton
                        onClick={handleOpenUploadDialog}
                        text={intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-upload' })}
                        icon={UploadIcon}
                    />
                )}
                {!isItemSelected() && (
                    <Box
                        onClick={(e) => {
                            e?.stopPropagation();
                            handleOpenSortByMenu(e);
                        }}
                    >
                        <FileMngrToolbarButton
                            text={intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-sortby' })}
                            icon={SortIcon}
                            hasMenu
                        />
                    </Box>
                )}
                {!isItemSelected() && !workWithTemp && (
                    <FileMngrToolbarButton
                        onClick={handleRefreshFolder}
                        text={intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-refresh' })}
                        icon={RefreshIcon}
                    />
                )}
                {((isItemSelected() && canEdit) || itemToPaste !== null) && <Box width={2} height={10} bgcolor="#dee3ed" mx={2} />}
                {!isModal && isItemSelected() && canEdit && (
                    <FileMngrToolbarButton
                        onClick={() => handleCutItem(selectedItem, getItemParentPath(selectedItem))}
                        text={intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-cut' })}
                        icon={ContentCutIcon}
                        disabled={isSystemFolder(selectedItem?.name)}
                    />
                )}
                {!isModal && isItemSelected() && canEdit && (
                    <FileMngrToolbarButton
                        onClick={() => handleCopyItem(selectedItem, getItemParentPath(selectedItem))}
                        text={intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-copy' })}
                        icon={FileCopyIcon}
                        disabled={isSystemFolder(selectedItem?.name)}
                    />
                )}
                {canEdit && (itemToPaste !== null || dialogItemToPaste != null) ? (
                    <FileMngrToolbarButton
                        onClick={handlePasteItem}
                        text={intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-paste' })}
                        icon={ContentPasteIcon}
                        disabled={canPaste() || isPasting || isSystemFolder(selectedItem?.name)}
                        isLoading={isPasting}
                    />
                ) : (
                    <></>
                )}

                {canEdit && itemToPaste !== null ? (
                    <FileMngrToolbarButton
                        onClick={handleSpecialPasteItem}
                        text={intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-paste-special' })}
                        icon={ContentPasteGoTwoTone}
                        disabled={canPaste() || isPasting || isSystemFolder(selectedItem?.name)}
                    />
                ) : (
                    <></>
                )}
                {!isModal && isItemSelected() && <Box width={2} height={10} bgcolor="#dee3ed" mx={2} />}
                {!isModal && isItemSelected() && canDelete && (
                    <FileMngrToolbarButton
                        onClick={handleDelete}
                        text={intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-delete' })}
                        icon={DeleteIcon}
                        disabled={isSystemFolder(selectedItem?.name)}
                    />
                )}
                {!isModal && isItemSelected() && selectedItem && (
                    <FileMngrToolbarButton
                        onClick={handleDownload}
                        text={intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-download' })}
                        icon={DownloadingTwoTone}
                        disabled={isDownloading}
                        isLoading={isDownloading}
                    />
                )}
                <Box flexGrow={1} sx={{ [theme.breakpoints.down('sm')]: { display: 'none' } }} />
                {!isModal && isItemSelected() && isMmDown && (
                    <Box sx={{ [theme.breakpoints.down('md')]: { display: 'none' } }}>
                        <FileMngrToolbarButton
                            onClick={handleUnselectItem}
                            text={intl.formatMessage({ id: 'ui-comp-filemngr-toolbar-button-unselect' })}
                            icon={ClearAll}
                        />
                    </Box>
                )}
                {isItemSelected() && canEdit && <Box width={2} height={10} bgcolor="#dee3ed" mx={2} />}
                {multipleSelectedItems?.length === 0 && (
                    <FileMngrToolbarButton onClick={handleOpenViewDetailsDialog} icon={InfoIcon} disabled={!isItemSelected()} />
                )}
            </FileMngrToolbar>
            {/* SECOND TOOLBAR */}
            {theme.breakpoints.down('md') && (
                <Stack
                    pl={1.5}
                    pr={1}
                    direction="row"
                    alignItems="center"
                    height={33}
                    sx={{
                        [theme.breakpoints.down('md')]: {
                            display: 'flex',
                            overflowX: 'auto'
                        },
                        [theme.breakpoints.up('md')]: { display: 'none' }
                    }}
                >
                    <Box>
                        <FileMngrPathBreadcrumb
                            path={currentPath}
                            currentPath={currentPath}
                            generatePath={generatePath}
                            handleReadFolder={handleReadFolder}
                            workWithTemp={Boolean(workWithTemp)}
                        />
                    </Box>
                </Stack>
            )}
            <Stack
                borderTop={`0.2px solid ${BORDER_COLOR}`}
                borderBottom={`0.2px solid ${BORDER_COLOR}`}
                pl={1.5}
                pr={1}
                direction="row"
                alignItems="center"
                height={33}
            >
                {theme.breakpoints.up('md') && (
                    <Box>
                        <FileMngrPathBreadcrumb
                            path={currentPath}
                            currentPath={currentPath}
                            generatePath={generatePath}
                            handleReadFolder={handleReadFolder}
                            workWithTemp={Boolean(workWithTemp)}
                        />
                    </Box>
                )}

                <Box flexGrow={1} />
                {Array.isArray(currentPath) &&
                    currentPath?.length >= 1 &&
                    currentPath?.name !== '' &&
                    folderContent &&
                    folderContent?.files &&
                    folderContent?.files?.length > 0 && (
                        <Box component="form" onSubmit={handleSubmitSearch}>
                            <TextField
                                placeholder={`${intl.formatMessage({ id: 'ui-comp-filemngr-statusbar-search-textfield-placeholder' })} ${
                                    isLoading || workWithTemp ? '' : currentPath[currentPath?.length - 1].name
                                }`}
                                variant="standard"
                                size="small"
                                sx={{ fontSize: 9 }}
                                InputProps={{
                                    style: { padding: '0px 2px' },
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ fontSize: 18 }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {/* {isLoading && <CircularProgress size={18} />} */}
                                            {!isLoading && searchText !== '' && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        handleClearSearchText();
                                                    }}
                                                >
                                                    <ClearIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            )}
                                        </InputAdornment>
                                    )
                                }}
                                value={searchText}
                                onChange={(e) => {
                                    handleChangeSearchText(e);
                                }}
                            />
                        </Box>
                    )}
            </Stack>
            <Stack
                component="div"
                flexGrow={1}
                onClick={() => {
                    setSelectedItem(null);
                    setMultipleSelectedItems([]);
                }}
                onContextMenu={(e) => {
                    e?.preventDefault();
                }}
                sx={{
                    cursor: 'context-menu',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 320,
                    height: '100%'
                }}
            >
                {!isLoading && folderContentReference() && folderContentReference()?.files?.length > 0 && (
                    <TableContainer sx={{ flex: 1 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell align="left">{intl.formatMessage({ id: 'traits-appentityisdescribable-name' })}</TableCell>
                                    <TableCell align="left">{intl.formatMessage({ id: 'traits-appentity-createdat' })}</TableCell>
                                    <TableCell align="left">{intl.formatMessage({ id: 'traits-appentity-createdby' })}</TableCell>
                                    <TableCell align="left">{intl.formatMessage({ id: 'traits-appentity-updatedat' })}</TableCell>
                                    <TableCell align="left">{intl.formatMessage({ id: 'traits-appentity-updatedby' })}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {folderContentReference() &&
                                    Array.isArray(folderContentReference()?.files) &&
                                    sortItems(
                                        folderContentReference()?.files.filter((item) => item.isFile === false),
                                        sortBy,
                                        sortType
                                    ).map((item, index) => (
                                        <FileMngrTableRowItem
                                            key={index}
                                            name={changeFileMngrItemName(item.name, intl)}
                                            searchText={searchText}
                                            isFMItemCutted={isFMItemCutted(itemIdGenerator({ isFile: false, name: item.name, index }))}
                                            isFile={false}
                                            isSelected={isSelected(false, item?.name ?? '', index)}
                                            format={typeof item.type === 'string' && item.type.trim() !== '' ? item.type : null}
                                            onClick={(event) => handleItemSelection(event, item, false, index)}
                                            onDoubleClick={(e) => {
                                                e.stopPropagation();
                                                openFolder(item);
                                            }}
                                            handlePaste={(data) => {
                                                // if (`${getCurrentPath()}${item?.name}/` !== `${getCurrentPath()}${data?.name}/`)
                                                handleRemoveFromContent(false, item.name, index);

                                                handlePasteItem({
                                                    action: data?.action,
                                                    data: data?.data,
                                                    names: data?.names,
                                                    path: getCurrentPath(),
                                                    renameFiles: [],
                                                    targetData: data?.targetData,
                                                    targetPath: `${getCurrentPath()}${item?.name}/`
                                                });
                                            }}
                                            itemData={item}
                                            path={`${currentPath[currentPath?.length - 1]?.name ?? ''}${getCurrentPath()}/${
                                                item?.filterPath ?? ''
                                            }`.replace('//', '/')}
                                            apiGetImageUrl={fileMngrUrls.getImageUrl}
                                            setContextMenu={setContextMenu}
                                            setIsLoading={setIsLoading}
                                            isSystemFolder={isSystemFolder(item?.name)}
                                        />
                                    ))}
                                {folderContentReference() &&
                                    Array.isArray(folderContentReference()?.files) &&
                                    sortItems(
                                        folderContentReference()?.files.filter((item) => item.isFile === true),
                                        sortBy,
                                        sortType
                                    ).map((item, index) => (
                                        <FileMngrTableRowItem
                                            key={index}
                                            name={changeFileMngrItemName(item.name, intl)}
                                            searchText={searchText}
                                            isFMItemCutted={isFMItemCutted(itemIdGenerator({ isFile: true, name: item.name, index }))}
                                            isFile
                                            isSelected={isSelected(true, item?.name ?? '', index)}
                                            format={typeof item.type === 'string' && item.type.trim() !== '' ? item.type : null}
                                            onClick={(event) => handleItemSelection(event, item, true, index)}
                                            onDoubleClick={(e) => {
                                                e.stopPropagation();
                                                handleDownload(item);
                                            }}
                                            handlePaste={(data) => {
                                                // if (`${getCurrentPath()}${item?.name}/` !== `${getCurrentPath()}${data?.name}/`)
                                                handleRemoveFromContent(true, item.name, index);

                                                handlePasteItem({
                                                    action: data?.action,
                                                    data: data?.data,
                                                    names: data?.names,
                                                    path: getCurrentPath(),
                                                    renameFiles: [],
                                                    targetData: data?.targetData,
                                                    targetPath: `${getCurrentPath()}${item?.name}/`
                                                });
                                            }}
                                            itemData={item}
                                            path={`${currentPath[currentPath?.length - 1]?.name ?? ''}${getCurrentPath()}/${
                                                item?.filterPath ?? ''
                                            }`.replace('//', '/')}
                                            apiGetImageUrl={getImageUrl}
                                            setContextMenu={setContextMenu}
                                            setIsLoading={setIsLoading}
                                            isSystemFolder={isSystemFolder(item?.name)}
                                        />
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {isLoading === true && (
                    <Stack flexGrow={1} justifyContent="center" alignItems="center" bgcolor="transparent">
                        <CircularProgress color="primary" />
                    </Stack>
                )}
                {!isLoading && folderContentReference() && folderContentReference()?.files?.length === 0 && searchText?.length === 0 && (
                    <Stack flexGrow={1} justifyContent="center" alignItems="center" bgcolor="transparent">
                        <FolderIcon sx={{ fontSize: 100 }} />
                        <Typography variant="subtitle1" fontSize={20}>
                            {intl.formatMessage({ id: 'ui-comp-filemngr-empty-emptyfolder-titlemsg' })}
                        </Typography>
                        <Typography variant="body1">{intl.formatMessage({ id: 'ui-comp-filemngr-empty-emptyfolder-bodymsg' })}</Typography>
                    </Stack>
                )}
                {!isLoading && folderContentReference() && folderContentReference()?.files?.length === 0 && searchText?.length > 0 && (
                    <Stack flexGrow={1} justifyContent="center" alignItems="center" bgcolor="transparent">
                        <SearchIcon sx={{ fontSize: 100 }} />
                        <Typography
                            component="div"
                            variant="subtitle1"
                            fontSize={20}
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <span>{`"`}</span>
                            <Typography
                                variant="subtitle1"
                                fontSize={20}
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    textWrap: 'nowrap',
                                    maxWidth: 300
                                }}
                            >
                                {searchText}
                            </Typography>
                            <span>{`"`}&nbsp;</span>
                            {intl.formatMessage({ id: 'ui-comp-filemngr-empty-search-notfound' })}
                        </Typography>
                    </Stack>
                )}
            </Stack>
            {fileMngrRenderSortByMenu(SORT_BY_MENU, sortBy, sortType, setSortBy, setSortType, handleCloseSortByMenu, sortByMenuAnchorEl)}
            {fileMngrRenderContextMenu(
                selectedItem?.isFile === true ? FILE_CONTEXT_MENU : FOLDER_CONTEXT_MENU,
                selectedItem !== null ? contextMenu : null,
                setContextMenu
            )}
            {/* {renderContextMenu()} */}
            <FileMngrDialogUpload
                openUploadDialog={openUploadDialog}
                handleCloseUploadDialog={handleCloseUploadDialog}
                handleRefreshFolder={handleRefreshFolder}
                currentPath={getCurrentPath()}
                url={fileMngrUrls.uploadUrl}
            />
            <FileMngrDialogRename
                openRenameDialog={openRenameDialog}
                handleCloseRenameDialog={handleCloseRenameDialog}
                handleRefreshFolder={handleRefreshFolder}
                currentPath={getCurrentPath()}
                selectedItem={selectedItem}
                url={fileMngrUrls.url}
            />
            <FileMngrDialogViewDetails
                openViewDetailsDialog={openViewDetailsDialog}
                handleCloseViewDetailsDialog={handleCloseViewDetailsDialog}
                selectedItem={selectedItem}
                currentPath={getCurrentPath()}
                url={fileMngrUrls.url}
            />
            <FileMngrDialogNewFolder
                openNewFolderDialog={openNewFolderDialog}
                handleCloseNewFolderDialog={handleCloseNewFolderDialog}
                handleRefreshFolder={handleRefreshFolder}
                currentPath={getCurrentPath()}
                url={fileMngrUrls.url}
            />
            <FileMngrDialogDeleteConfirmation
                deleteDialogOpen={deleteDialogOpen}
                handleDeleteDialogClose={handleDeleteDialogClose}
                deleteItem={() =>
                    deleteItem({
                        item: selectedItem,
                        items: multipleSelectedItems,
                        setItem: setSelectedItem,
                        setItems: setMultipleSelectedItems
                    })
                }
                deletedItems={multipleSelectedItems}
                url={fileMngrUrls.url}
            />
            <FileMngrDialogSpecialPaste
                url={fileMngrUrls.url}
                uploadUrl={fileMngrUrls.uploadUrl}
                downloadUrl={fileMngrUrls.downloadUrl}
                getImageUrl={fileMngrUrls.getImageUrl}
                permissionData={permissionData}
                dataToPaste={itemToPaste}
                clearDialogItemToPaste={() => {
                    setItemToPaste(null);
                }}
                openSpecialPasteDialog={openSpecialPasteDialog}
                setOpenSpecialPasteDialog={setOpenSpecialPasteDialog}
            />
        </FileMngrContainer>
    );
};

FileMngr.propTypes = {
    url: PropTypes.string,
    uploadUrl: PropTypes.string,
    downloadUrl: PropTypes.string,
    getImageUrl: PropTypes.string,
    permissionData: PropTypes.array,
    isModal: PropTypes.bool,
    dialogItemToPaste: PropTypes.any,
    clearDialogItemToPaste: PropTypes.func,
    targetClass: PropTypes.string,
    targetId: PropTypes.number,
    workWithTemp: PropTypes.bool,
    forceReload: PropTypes.bool,
    onChangeTempFoldersName: PropTypes.func
};

export default FileMngr;
